import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Download,
  FileText,
  BookOpen,
  Loader2,
  ShoppingBag,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  IndianRupee,
  Calendar,
  ArrowLeft,
} from "lucide-react";

export default function PurchaseHistory() {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please login to view your purchases");
        navigate("/login");
        return;
      }

      const response = await fetch(`${API}/shop/purchases`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPurchases(data.purchases || []);
      } else if (response.status === 401) {
        toast.error("Session expired. Please login again.");
        navigate("/login");
      } else {
        toast.error("Failed to load purchases");
      }
    } catch (error) {
      console.error("Error fetching purchases:", error);
      toast.error("Error loading purchase history");
    }
    setIsLoading(false);
  };

  const handleDownload = async (orderId, productName) => {
    setDownloadingId(orderId);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/shop/download/${orderId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Eduzy_${productName.replace(/\s+/g, "_")}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Download started!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Download failed");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download PDF");
    }
    setDownloadingId(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3" />
            Completed
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
      case "failed":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <XCircle className="w-3 h-3" />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            {status}
          </span>
        );
    }
  };

  // Group purchases by bulk_order_id if present
  const groupedPurchases = purchases.reduce((acc, purchase) => {
    const groupKey = purchase.bulk_order_id || purchase.order_id;
    if (!acc[groupKey]) {
      acc[groupKey] = {
        bulk_order_id: purchase.bulk_order_id,
        created_at: purchase.created_at,
        items: [],
        total: 0,
      };
    }
    acc[groupKey].items.push(purchase);
    acc[groupKey].total += purchase.price;
    return acc;
  }, {});

  const purchaseGroups = Object.values(groupedPurchases).sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-600 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white/80 hover:text-white hover:bg-white/20 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
            <ShoppingBag className="w-8 h-8" />
            My Purchases
          </h1>
          <p className="text-white/80 mt-2">
            Download your purchased worksheets and question banks anytime
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Refresh Button */}
        <div className="flex justify-end mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchPurchases}
            disabled={isLoading}
            className="text-gray-600"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            <span className="ml-3 text-gray-600">Loading your purchases...</span>
          </div>
        ) : purchaseGroups.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <ShoppingBag className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No purchases yet</h3>
              <p className="text-gray-500 mb-4">
                Start building your learning library with worksheets and question banks
              </p>
              <Button
                onClick={() => navigate("/eduquest")}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Browse Learning Shop
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {purchaseGroups.map((group) => (
              <Card key={group.bulk_order_id || group.items[0]?.order_id} className="overflow-hidden">
                <CardHeader className="bg-gray-50 border-b border-gray-200 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {formatDate(group.created_at)}
                      </span>
                      {group.bulk_order_id && (
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                          Cart Order
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <span className="text-sm">{group.items.length} item(s)</span>
                      <span className="font-bold flex items-center">
                        <IndianRupee className="w-3 h-3" />
                        {group.total}
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-gray-100">
                    {group.items.map((purchase) => (
                      <div
                        key={purchase.order_id}
                        className="flex flex-wrap items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
                      >
                        {/* Icon */}
                        <div
                          className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            purchase.product_type === "worksheet"
                              ? "bg-blue-100"
                              : "bg-purple-100"
                          }`}
                        >
                          {purchase.product_type === "worksheet" ? (
                            <FileText className="w-6 h-6 text-blue-600" />
                          ) : (
                            <BookOpen className="w-6 h-6 text-purple-600" />
                          )}
                        </div>

                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-800">
                            {purchase.product_name}
                          </h4>
                          <p className="text-sm text-gray-500">
                            Class {purchase.grade} • {purchase.subject?.charAt(0).toUpperCase() + purchase.subject?.slice(1)}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            {getStatusBadge(purchase.payment_status)}
                            <span className="text-xs text-gray-400">
                              Order: {purchase.order_id}
                            </span>
                          </div>
                        </div>

                        {/* Price */}
                        <div className="text-right">
                          <span
                            className={`font-bold flex items-center justify-end ${
                              purchase.product_type === "worksheet"
                                ? "text-blue-600"
                                : "text-purple-600"
                            }`}
                          >
                            <IndianRupee className="w-3 h-3" />
                            {purchase.price}
                          </span>
                        </div>

                        {/* Download Button */}
                        <div>
                          {purchase.payment_status === "completed" ? (
                            <Button
                              onClick={() => handleDownload(purchase.order_id, purchase.product_name)}
                              disabled={downloadingId === purchase.order_id}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              {downloadingId === purchase.order_id ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-2" />
                                  Download PDF
                                </>
                              )}
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              disabled
                              className="text-gray-400"
                            >
                              {purchase.payment_status === "pending" ? "Payment Pending" : "Unavailable"}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
